import { Component, h, Host, Method, Prop, State, Watch } from '@stencil/core';
import wcShared from '@web-components/shared';

import { FreeProducts, Products } from '../constants/product';
import { ProductNames } from '../constants/productName';
import {
  disableUnavailableProducts,
  enrichOrganizationsWithFlags,
  getProductsAccess,
  removeDisabledProducts,
} from '../utils/product.utils';

@Component({
  tag: 'module-products-component',
  styleUrl: 'module-products-component.scss',
  shadow: true,
})
export class ModuleProductsComponent {
  @Prop() readonly currentOrganizationId: number = wcShared.store.currentSpace?.id;

  @State() theme: 'light' | 'dark' = wcShared.store.theme;
  @State() isVisible: boolean;
  @State() isLoading = false;
  @State() hasData = false;
  @State() products = Products as Partial<Products>;

  private organizations: IWCSpace[];
  private handleThemeChange = (value: 'light' | 'dark') => {
    this.theme = value;
  };
  private panelRef: { visible: boolean; close: () => void; open: () => void };
  private isUserConnected = Object.keys(wcShared.store.userStartConfig).length > 0;

  @Watch('currentOrganizationId')
  disableUnavailableProducts(currentOrganizationId: number) {
    if (this.organizations) {
      const currentOrganization =
        typeof currentOrganizationId === 'number'
          ? this.organizations.find((org) => org.id === currentOrganizationId)
          : undefined;

      const productsAccess = getProductsAccess(this.organizations, currentOrganization);
      this.products = disableUnavailableProducts(this.products, productsAccess);
      this.products = removeDisabledProducts(this.products, currentOrganization);
    }
  }

  componentWillLoad() {
    if (!this.isUserConnected) this.products = FreeProducts;
  }

  componentDidLoad() {
    wcShared.store.addObserver('theme', this.handleThemeChange);
  }

  disconnectedCallback() {
    wcShared.store.removeObserver('theme', this.handleThemeChange);
  }

  private onOpenPanel = () => {
    this.isVisible = true;
    if (this.isUserConnected && !this.hasData) {
      void this.getData();
    }
  };

  private async getData(): Promise<void> {
    this.isLoading = true;
    try {
      const response = await wcShared.httpCall(
        'GET',
        '/api/components/profile/me?with=spaces,has_ws_sms',
      );

      if (response.status === 200) {
        const data = await response.json();

        if (data?.data) {
          this.hasData = true;

          this.organizations = enrichOrganizationsWithFlags(data.data.spaces);
          const hasSms = data.data.user.access.has_ws_sms;
          if (!hasSms) {
            const { [ProductNames.SMS]: _, ...filteredProducts } = this.products;
            this.products = filteredProducts as Products;
          }

          this.disableUnavailableProducts(this.currentOrganizationId);
        }
      }
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      this.isLoading = false;
    }
  }

  /**
   * Open the panel
   */
  @Method()
  async open() {
    void this.panelRef.open();
  }

  /**
   * Close the panel
   */
  @Method()
  async close() {
    void this.panelRef.close();
  }

  render() {
    return (
      <Host
        role="button"
        aria-label={wcShared.translate('Ouvrir le menu des applications')}
      >
        <wc-panel
          ref={(ref) => (this.panelRef = ref)}
          onOpen={this.onOpenPanel}
          onClose={() => {
            this.isVisible = false;
          }}
        >
          <div
            aria-haspopup="true"
            aria-expanded="false"
            slot="trigger"
          >
            <slot name="trigger"></slot>
          </div>

          <products-panel
            class={`theme-${this.theme}`}
            closeLayout={() => this.panelRef?.close()}
            isVisible={this.isVisible}
            isLoading={this.isLoading}
            products={Object.values(this.products)}
            organizationId={this.currentOrganizationId}
          />
        </wc-panel>
      </Host>
    );
  }
}
